/**
 * Legacy dialog component - marked for replacement with AddUserRoleDialog
 * TODO: Replace with generic AddUserRoleDialog once config factory is updated
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useInviteAdmin } from '../hooks/useSystemAdmins';
import { useUserExists } from '@shared/hooks/useUserExists';
import { useAuthContext } from '@shared/components';
import { AdminInviteFormData } from '../types';

interface AddAdminDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const AddAdminDialog: React.FC<AddAdminDialogProps> = ({ open, onClose, onSuccess }) => {
  const { getSystemAdminTenantId } = useAuthContext();
  const inviteAdminMutation = useInviteAdmin();
  const [emailForCheck, setEmailForCheck] = React.useState('');
  const debouncedEmail = useDebounce(emailForCheck, 500);

  const { data: userExistsData, isLoading: isCheckingUser } = useUserExists(debouncedEmail, {
    enabled: debouncedEmail.length > 0 && debouncedEmail.includes('@'),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<AdminInviteFormData>({
    defaultValues: {
      email: '',
      name: '',
    },
  });

  const email = watch('email');

  React.useEffect(() => {
    setEmailForCheck(email);
  }, [email]);

  const handleClose = () => {
    reset();
    setEmailForCheck('');
    onClose();
  };

  const onSubmit = async (data: AdminInviteFormData) => {
    try {
      await inviteAdminMutation.mutateAsync({
        email: data.email,
        name: data.name || undefined,
        tenantId: getSystemAdminTenantId(),
      });

      // Close dialog and reset form on success
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error('Invite admin failed:', error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Add System Administrator</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email Address"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  required
                />
              )}
            />

            {isCheckingUser && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Checking user...
                </Typography>
              </Box>
            )}

            {userExistsData && (
              <Alert severity={userExistsData.exists ? 'info' : 'warning'}>
                {userExistsData.exists ? 'User exists in the system' : 'New user will be created'}
              </Alert>
            )}

            <Controller
              name="name"
              control={control}
              rules={{
                required:
                  userExistsData?.exists === false ? 'Name is required for new users' : false,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Full Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={
                    errors.name?.message ||
                    (userExistsData?.exists
                      ? 'Optional for existing users'
                      : 'Required for new users')
                  }
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={inviteAdminMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={inviteAdminMutation.isPending}>
            {inviteAdminMutation.isPending ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              'Send Invitation'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
